import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { therapyTypes } from '../data/mockData';

const PersonalTherapyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get the personal therapy data
  const personalTherapy = therapyTypes.find(therapy => therapy.id === 3);

  return (
    <div className="pt-32 pb-16">
      <div className="section-container">
        {/* Hero section */}
        <div className="flex flex-col lg:flex-row gap-12 mb-16">
          <motion.div 
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-sharon-dark-green mb-6">
              {personalTherapy.title}
            </h1>
            <div className="text-lg text-sharon-gray mb-8 whitespace-pre-line">
              {personalTherapy.fullDescription}
            </div>
            <div className="mt-8">
              <Link to="/" className="sharon-btn">
                חזרה לדף הבית
              </Link>
            </div>
          </motion.div>

          <motion.div 
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src={personalTherapy.imageSrc} 
                alt={personalTherapy.title} 
                className="w-full h-auto"
              />
            </div>
          </motion.div>
        </div>

        {/* Approach section */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2 className="text-2xl font-semibold text-sharon-dark-green mb-6">
            הגישה הטיפולית שלי
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-3">טיפול דינמי</h3>
              <p className="text-sharon-gray">
                הגישה הדינמית מתמקדת בהבנת התהליכים הפנימיים וחקר העבר כדי להבין את ההווה. באמצעות טיפול זה, אנו חוקרים יחד דפוסים רגשיים והתנהגותיים שמקורם בחוויות עבר, ומפתחים מודעות לדפוסים אלה כדי לאפשר צמיחה ושינוי.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-3">טיפול קוגניטיבי-התנהגותי (CBT)</h3>
              <p className="text-sharon-gray">
                גישת ה-CBT מתמקדת בזיהוי ושינוי דפוסי חשיבה והתנהגות מגבילים. הטיפול כולל לימוד טכניקות ספציפיות להתמודדות עם מצבים מאתגרים, ויישום אסטרטגיות יעילות לניהול מחשבות ורגשות.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-3">טיפול ממוקד רגש (EFT)</h3>
              <p className="text-sharon-gray">
                ב-EFT, אנו מתמקדים ברגשות כמפתח לשינוי. הטיפול עוזר לזהות, להבין, ולעבד רגשות באופן עמוק יותר, תוך התמודדות עם צרכים רגשיים בלתי מסופקים ויצירת דפוסי התקשרות בריאים יותר.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-3">טיפול מבוסס מיינדפולנס</h3>
              <p className="text-sharon-gray">
                גישה זו משלבת טכניקות של קשיבות ומודעות ברגע ההווה. הטיפול מסייע בפיתוח מיומנויות להתבוננות ללא שיפוט, הפחתת מתח וחרדה, ויצירת מרחב פנימי רגוע יותר.
              </p>
            </div>
          </div>
        </motion.div>

        {/* What to expect section */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h2 className="text-2xl font-semibold text-sharon-dark-green mb-6">
            מה לצפות בטיפול אישי
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-sharon-light-green rounded-full flex items-center justify-center mb-4">
                <span className="text-sharon-dark-green text-xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-3">פגישת היכרות</h3>
              <p className="text-sharon-gray">
                נפגש לשיחת היכרות ראשונית בה נכיר זה את זה, תספרו על עצמכם ועל מה שהביא אתכם לטיפול, ונגדיר יחד את מטרות הטיפול.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-sharon-light-green rounded-full flex items-center justify-center mb-4">
                <span className="text-sharon-dark-green text-xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-3">תהליך הטיפול</h3>
              <p className="text-sharon-gray">
                נפגש באופן קבוע, בדרך כלל אחת לשבוע. במהלך המפגשים נעבוד יחד על הבנת הקשיים, זיהוי דפוסים, ופיתוח דרכי התמודדות חדשות.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-sharon-light-green rounded-full flex items-center justify-center mb-4">
                <span className="text-sharon-dark-green text-xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-3">התקדמות והערכה</h3>
              <p className="text-sharon-gray">
                לאורך התהליך נעקוב יחד אחר ההתקדמות, נעריך את השינויים שחלים, ונתאים את הטיפול בהתאם לצרכים המשתנים.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Contact CTA */}
        <motion.div
          className="mt-16 bg-sharon-light-green p-8 rounded-lg text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <h2 className="text-2xl font-semibold text-sharon-dark-green mb-4">
            מעוניינים להתחיל תהליך של צמיחה אישית?
          </h2>
          <p className="text-lg text-sharon-gray mb-6">
            אני כאן כדי ללוות אתכם בדרך. צרו איתי קשר לקביעת פגישת היכרות ראשונית.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 sm:space-x-reverse">
            <a
              href="tel:+972526507776"
              className="sharon-btn text-lg px-6 py-2 rounded-md"
            >
              התקשרו עכשיו: 0526507776
            </a>
            <a
              href="mailto:sharon.weisfeld@gmail.com"
              className="text-sharon-dark-green hover:text-sharon-green transition-colors duration-300 text-lg"
            >
              שלחו מייל
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PersonalTherapyPage;